import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { submitContactForm } from '../utils/api';
import { useToast } from '../contexts/ToastContext';

const Contact: React.FC = () => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: 'General Inquiry',
    message: '',
    privacy: false
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFaq, setShowFaq] = useState(false);
  
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    });
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.name || !formData.email || !formData.subject || !formData.message || !formData.privacy) {
      showToast('Please fill all required fields.', 'error');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit form to the backend API
      await submitContactForm({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        subject: formData.subject,
        message: formData.message
      });
      
      // Show success message
      showToast('Thank you! Your message has been sent successfully.', 'success');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        subject: 'General Inquiry',
        message: '',
        privacy: false
      });
    } catch (error: any) {
      console.error('Error submitting form:', error);
      showToast(error.message || 'Something went wrong. Please try again later.', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const faqItems = [
    {
      question: "What are the requirements to study in Japan?",
      answer: "Requirements typically include a valid passport, student visa, acceptance letter from a Japanese institution, proof of financial stability, and in some cases, Japanese language proficiency certification depending on your program of study."
    },
    {
      question: "How much does it cost to study in Japan?",
      answer: "Tuition fees range from ¥535,800 to ¥1,725,000 per year for undergraduate programs at public universities. Private universities can cost between ¥700,000 to ¥3,000,000 annually. Living expenses average ¥100,000 to ¥150,000 per month depending on the location."
    },
    {
      question: "Are there scholarships available for international students?",
      answer: "Yes, several scholarships are available, including the MEXT Scholarship from the Japanese government, JASSO Student Exchange Support Program, and various university-specific and private foundation scholarships."
    },
    {
      question: "Do I need to know Japanese to study in Japan?",
      answer: "While many universities offer programs in English, having Japanese language skills is beneficial for daily life and increases your study opportunities. Some programs require Japanese proficiency, while others include language courses as part of the curriculum."
    },
    {
      question: "What types of accommodation are available for students?",
      answer: "Options include university dormitories, private apartments (both furnished and unfurnished), shared houses/apartments, homestays with Japanese families, and student-specific housing complexes managed by educational institutions or private companies."
    }
  ];

  return (
    <section id="contact" className="min-h-screen relative bg-gradient-to-b from-blue-50 via-white to-pink-50 overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden opacity-10">
        <div className="japanese-pattern"></div>
      </div>
      
      {/* Cherry blossom animated particles */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div className="cherry-blossom-container"></div>
      </div>
      
      {/* Decorative blobs */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-gradient-to-br from-primary/30 to-secondary/30 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-gradient-to-tr from-secondary/30 to-primary/30 rounded-full blur-3xl"></div>
      
      {/* Hero header section */}
      <div className="relative z-10 pt-24 pb-8 md:pt-32 md:pb-12">
        <div className="container mx-auto px-4">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-block px-6 py-2 bg-gradient-to-r from-primary/10 to-secondary/10 backdrop-blur-sm text-primary rounded-full text-sm font-semibold mb-6 border border-primary/20">
              Get In Touch With Us
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary leading-tight">
              How Can We Help You?
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              Have questions about studying in Japan? Our team is here to guide you through your educational journey with expert advice and support.
            </p>
          </motion.div>
        </div>
      </div>
      
      {/* Map section - Prominent full width */}
      <div className="relative z-10 py-8 md:py-12 px-4 md:px-0">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="bg-white/80 backdrop-blur-md rounded-3xl p-6 md:p-8 shadow-xl border border-gray-100 overflow-hidden"
          >
            <div className="text-center max-w-2xl mx-auto mb-8">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                Visit Our Office in Kathmandu, Nepal
              </h2>
              <p className="text-gray-700">
                Our office is located in the heart of Kathmandu, easily accessible by public transport.
              </p>
            </div>
            
            <div className="rounded-2xl overflow-hidden h-[400px] md:h-[500px] relative shadow-2xl border-4 border-white">
              {/* Map iframe placeholder - will be replaced later */}
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3531.420238020654!2d85.31020947626529!3d27.73518142426174!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb190029fb1633%3A0xe0ae60ee6c3650f2!2sRecall%20Business%20Group%20Pvt.%20Ltd.!5e0!3m2!1sen!2snp!4v1743278032931!5m2!1sen!2snp" 
                width="100%" 
                height="100%" 
                style={{ border: 0 }} 
                allowFullScreen 
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Our location"
                className="absolute inset-0"
              ></iframe>
              
              {/* Enhanced floating card on the map */}
              <div className="absolute top-6 left-6 bg-white/90 backdrop-blur-md p-5 rounded-xl shadow-2xl border-2 border-primary/20 max-w-xs transform transition-transform hover:scale-105">
                <div className="flex items-center space-x-3 mb-2">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-white text-xl shadow-md">
                    🏢
                  </div>
                  <h3 className="font-bold text-primary text-lg">Recall Nepal</h3>
                </div>
                <p className="text-gray-700 font-medium">Tokha-9, Gongabu, Kathmandu, Nepal</p>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="mr-2">📞</span> +977 014986209
                  </div>
                </div>
              </div>
              
              {/* Map controls */}
              <div className="absolute bottom-6 right-6 flex flex-col space-y-2">
                <button className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-primary hover:bg-primary/5 transition-colors">
                  <span className="text-xl">+</span>
                </button>
                <button className="w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-primary hover:bg-primary/5 transition-colors">
                  <span className="text-xl">−</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
      
      {/* Main content section */}
      <div className="relative z-10 py-8 md:py-16">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12">
            {/* Contact Form */}
            <motion.div 
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.4 }}
              className="lg:col-span-7"
            >
              <div className="bg-white/90 backdrop-blur-md rounded-3xl p-6 md:p-10 shadow-xl border border-gray-100 relative overflow-hidden transform transition-all hover:shadow-2xl hover:-translate-y-1">
                {/* Glossy effect */}
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-primary/40 via-purple-200/40 to-secondary/40 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-tr from-secondary/40 via-indigo-200/40 to-primary/40 rounded-full blur-3xl"></div>
                
                <div className="relative z-10">
                  <div className="flex items-center space-x-4 mb-8">
                    <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-white text-2xl shadow-lg shadow-primary/30">
                      ✉️
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                        Send a Message
                      </h3>
                      <p className="text-gray-600">Fill the form below and we'll get back to you soon</p>
                    </div>
                  </div>
                  
                  <form className="space-y-6" onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="group">
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2 group-focus-within:text-primary transition-colors">
                          Full Name
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                            👤
                          </div>
                          <input
                            type="text"
                            id="name"
                            name="name"
                            value={formData.name}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all"
                            placeholder="Your Full Name"
                            required
                          />
                        </div>
                      </div>
                      
                      <div className="group">
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2 group-focus-within:text-primary transition-colors">
                          Email Address
                        </label>
                        <div className="relative">
                          <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                            📧
                          </div>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all"
                            placeholder="your@email.com"
                            required
                          />
                        </div>
                      </div>
                    </div>
                    
                    <div className="group">
                      <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2 group-focus-within:text-primary transition-colors">
                        Phone Number (Optional)
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          📱
                        </div>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all"
                          placeholder="+977 9800000000"
                        />
                      </div>
                    </div>
                    
                    <div className="group">
                      <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-2 group-focus-within:text-primary transition-colors">
                        Subject
                      </label>
                      <div className="relative">
                        <div className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400">
                          📋
                        </div>
                        <select
                          id="subject"
                          name="subject"
                          value={formData.subject}
                          onChange={handleInputChange}
                          className="w-full pl-10 pr-4 py-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all appearance-none"
                          style={{ backgroundImage: "url(\"data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e\")", backgroundPosition: "right 1rem center", backgroundRepeat: "no-repeat", backgroundSize: "1.5em 1.5em" }}
                        >
                          <option value="General Inquiry">General Inquiry</option>
                          <option value="Program Information">Program Information</option>
                          <option value="Application Support">Application Support</option>
                          <option value="Scholarship Information">Scholarship Information</option>
                          <option value="Visa Guidance">Visa Guidance</option>
                          <option value="Housing Support">Housing Support</option>
                        </select>
                      </div>
                    </div>
                    
                    <div className="group">
                      <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2 group-focus-within:text-primary transition-colors">
                        Your Message
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={5}
                        value={formData.message}
                        onChange={handleInputChange}
                        className="w-full px-4 py-4 rounded-xl bg-gray-50 border border-gray-200 text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all"
                        placeholder="Please describe how we can help you..."
                        required
                      ></textarea>
                    </div>
                    
                    <div className="flex items-center">
                      <input 
                        id="privacy" 
                        name="privacy" 
                        type="checkbox" 
                        checked={formData.privacy}
                        onChange={handleInputChange}
                        className="h-5 w-5 rounded border-gray-300 text-primary focus:ring-primary" 
                        required
                      />
                      <label htmlFor="privacy" className="ml-3 text-sm text-gray-600">
                        I agree to the <a href="#" className="text-primary hover:text-secondary font-medium">Privacy Policy</a>
                      </label>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className={`w-full px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-bold text-lg relative overflow-hidden group shadow-xl shadow-primary/20 hover:shadow-primary/40 transition-all hover:scale-[1.02] active:scale-[0.98] ${isSubmitting ? 'opacity-70 cursor-not-allowed' : ''}`}
                    >
                      {isSubmitting ? (
                        <>
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Sending...
                        </>
                      ) : (
                        <>
                          Send Message
                          <div className="absolute inset-0 scale-x-0 group-hover:scale-x-100 bg-white/20 origin-left transition-transform"></div>
                        </>
                      )}
                    </button>
                    
                    <p className="text-sm text-gray-500 text-center">
                      Your information is secure and encrypted. We never share your data.
                    </p>
                  </form>
                </div>
              </div>
            </motion.div>

            {/* Contact Information */}
            <motion.div 
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="lg:col-span-5 space-y-8"
            >
              {/* Contact Information */}
              <div className="bg-white/90 backdrop-blur-md rounded-3xl p-6 md:p-8 shadow-xl border border-gray-100 transform transition-all hover:shadow-2xl hover:-translate-y-1">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-white text-xl shadow-lg shadow-primary/30">
                    📞
                  </div>
                  <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                    Contact Details
                  </h3>
                </div>
                
                <div className="space-y-6">
                  <a 
                    href="mailto:info@recallnepal.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-4 p-4 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-2xl shadow-sm group-hover:shadow-md transition-all group-hover:scale-110">
                      📧
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1 text-gray-800">Email</h4>
                      <p className="text-primary font-medium">info@recallnepal.com</p>
                    </div>
                  </a>
                  
                  <a 
                    href="tel:+977 014986209"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-4 p-4 rounded-xl bg-gradient-to-r from-secondary/5 to-primary/5 hover:from-secondary/10 hover:to-primary/10 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-2xl shadow-sm group-hover:shadow-md transition-all group-hover:scale-110">
                      📱
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1 text-gray-800">Phone</h4>
                      <p className="text-secondary font-medium">+977 014986209</p>
                    </div>
                  </a>
                  
                  <a 
                    href="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3531.420238020654!2d85.31020947626529!3d27.73518142426174!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39eb190029fb1633%3A0xe0ae60ee6c3650f2!2sRecall%20Business%20Group%20Pvt.%20Ltd.!5e0!3m2!1sen!2snp!4v1743278032931!5m2!1sen!2snp"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center space-x-4 p-4 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5 hover:from-primary/10 hover:to-secondary/10 transition-all group"
                  >
                    <div className="w-12 h-12 rounded-xl bg-white flex items-center justify-center text-2xl shadow-sm group-hover:shadow-md transition-all group-hover:scale-110">
                      📍
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1 text-gray-800">Address</h4>
                      <p className="text-primary font-medium">Tokha-9, Gongabu, Kathmandu, Nepal</p>
                    </div>
                  </a>
                </div>
              </div>
              
              {/* Office Hours */}
              <div className="bg-white/90 backdrop-blur-md rounded-3xl p-6 md:p-8 shadow-xl border border-gray-100 transform transition-all hover:shadow-2xl hover:-translate-y-1">
                <div className="flex items-center space-x-4 mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-gradient-to-r from-primary to-secondary flex items-center justify-center text-white text-xl shadow-lg shadow-primary/30">
                    🕒
                  </div>
                  <h3 className="text-2xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
                    Office Hours
                  </h3>
                </div>
                
                <div className="space-y-4">
                  <div className="flex justify-between items-center p-4 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5">
                    <p className="text-gray-800 font-semibold">Sunday - Thursday</p>
                    <p className="text-secondary font-medium">9:00 AM - 5:00 PM</p>
                  </div>
                  <div className="flex justify-between items-center p-4 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5">
                    <p className="text-gray-800 font-semibold">Friday</p>
                    <p className="text-secondary font-medium">10:00 AM - 4:00 PM</p>
                  </div>
                  <div className="flex justify-between items-center p-4 rounded-xl bg-gradient-to-r from-primary/5 to-secondary/5">
                    <p className="text-gray-800 font-semibold">Saturday</p>
                    <p className="text-secondary font-medium">Closed</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
      
      {/* FAQ section */}
      {/* <motion.div 
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.8 }}
        className="relative z-10 py-16 bg-gradient-to-r from-primary/5 to-secondary/5 mt-16"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white/80 backdrop-blur-md rounded-3xl p-8 md:p-10 shadow-xl border border-gray-100 text-center">
            <div className="w-20 h-20 mx-auto bg-gradient-to-r from-primary to-secondary rounded-full flex items-center justify-center text-white text-3xl mb-6 shadow-lg shadow-primary/30">
              ❓
            </div>
            <h3 className="text-3xl md:text-4xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-primary to-secondary">
              Still Have Questions?
            </h3>
            <p className="text-xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Check out our frequently asked questions or reach out to our friendly team for any additional information you might need.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center space-y-4 sm:space-y-0 sm:space-x-6">
              <button 
                className="px-8 py-4 bg-gradient-to-r from-primary to-secondary text-white rounded-xl font-bold text-lg shadow-lg shadow-primary/30 hover:shadow-primary/50 transition-all hover:scale-105 min-w-[200px]"
                onClick={() => setShowFaq(!showFaq)}
              >
                {showFaq ? 'Hide FAQ' : 'View FAQ'}
              </button>
              <a href="tel:+977014986209" className="px-8 py-4 bg-white text-primary border-2 border-primary/20 rounded-xl font-bold text-lg shadow-md hover:shadow-lg transition-all hover:scale-105 min-w-[200px] flex items-center justify-center">
                <span className="mr-2">📞</span> Call Us
              </a>
            </div>
            
            {showFaq && (
              <motion.div 
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-12 text-left"
              >
                <div className="space-y-4">
                  {faqItems.map((item, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden border border-gray-100">
                      <details className="group">
                        <summary className="flex justify-between items-center p-6 cursor-pointer focus:outline-none">
                          <h4 className="text-lg font-semibold text-gray-800">{item.question}</h4>
                          <span className="ml-4 flex-shrink-0 h-5 w-5 text-primary relative">
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="absolute inset-0 transform group-open:rotate-180 transition-transform duration-300">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </span>
                        </summary>
                        <div className="px-6 pb-6 text-gray-600">
                          <p>{item.answer}</p>
                        </div>
                      </details>
                    </div>
                  ))}
                </div>
                <div className="mt-8 text-center">
                  <p className="text-gray-600">Don't see your question? <a href="#" className="text-primary font-medium hover:text-secondary">Contact us</a> directly.</p>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </motion.div> */}
    </section>
  );
};

export default Contact; 